import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    CircularProgress,
    Container,
    Stack,
    TextField,
} from '@mui/material';
import authService from '../auth/AuthService';
import PasswordResetSubmissionDTO from '../dtos/Users/PasswordResetSubmissionDto';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import AuthUtil from './Utils/AuthUtil';
import { useSnackbar } from './Common/SnackbarProvider';

export default function PasswordResetPage() {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const params = useParams();
    const token = params.token;

    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const navigate = useNavigate();

    const userToken = useSelector((state) => state.correcttrack.userToken);
    const isAuthorized = useMemo(() => AuthUtil.isAuthorized(userToken), [userToken]);

    // Inline validation flags — derived from current inputs so they update as the user types.
    const passwordTooShort = password.length > 0 && password.length < 6;
    const passwordsMismatch = Boolean(password) && Boolean(confirmPassword) && password !== confirmPassword;
    const canSubmit = password.length >= 6 && password === confirmPassword;

    async function resetPassword() {
        if (!canSubmit) return;

        const resetSubmission = new PasswordResetSubmissionDTO();
        resetSubmission.password = password;
        resetSubmission.token = token.toString();

        setSubmitting(true);
        const resetResponse = await authService.submitChangedPassword(resetSubmission);

        if (resetResponse.success) {
            snackbar.success(t('passwordResetPage.toast.success'));
            navigate('/');
        } else {
            snackbar.error(t('passwordResetPage.toast.error', { error: resetResponse.message ?? resetResponse }));
        }
        setSubmitting(false);
    }

    useEffect(() => {
        if (isAuthorized) {
            navigate('/Tasks');
        }
        document.title = 'CorrectTrack - Reset Password';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f5f5f5',
                padding: 2,
            }}
        >
            <Container maxWidth="sm">
                <Card elevation={8} sx={{ borderRadius: 2 }}>
                    <CardHeader
                        title={t('passwordResetPage.title')}
                        sx={{
                            bgcolor: 'primary.main',
                            color: 'primary.contrastText',
                            textAlign: 'center',
                            '& .MuiCardHeader-title': { fontSize: '1.5rem', fontWeight: 'bold' },
                        }}
                    />
                    <CardContent sx={{ p: 4 }}>
                        <Box component="form" onSubmit={(e) => { e.preventDefault(); resetPassword(); }}>
                            <Stack spacing={3}>
                                <TextField
                                    fullWidth
                                    type="password"
                                    label={t('passwordResetPage.field.newPassword')}
                                    autoComplete="new-password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    error={passwordTooShort}
                                    helperText={passwordTooShort ? t('passwordResetPage.validation.passwordTooShort') : ''}
                                />
                                <TextField
                                    fullWidth
                                    type="password"
                                    label={t('passwordResetPage.field.confirmNewPassword')}
                                    autoComplete="new-password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    error={passwordsMismatch}
                                    helperText={passwordsMismatch ? t('userMainSettings.validation.passwordsMustMatch') : ''}
                                />
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    disabled={!canSubmit || submitting}
                                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                                >
                                    {t('common.submit')}
                                </Button>
                            </Stack>
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
}

PasswordResetPage.propTypes = {
    isMobile: PropTypes.bool,
    isTablet: PropTypes.bool,
};
